// SIFT Data Types - Vehicle data structures

// User-assigned VIN status values
export type UserVinStatus = 'In Progress' | 'Done' | 'Sold / NA' | 'Updated' | 'Reinspection Needed' | 'Delete' | 'CR Reuse' | 'For Auction' | 'Client Elect, No List' | null;
export const USER_STATUS_OPTIONS: UserVinStatus[] = ['In Progress', 'Done', 'Sold / NA', 'Updated', 'Reinspection Needed', 'Delete', 'CR Reuse', 'For Auction', 'Client Elect, No List'];
/** The client chose not to list the unit (RDS-2.3). Prompts for an optional reason. */
export const CLIENT_ELECT_NO_LIST: UserVinStatus = 'Client Elect, No List';
/** Same limit as the client-status note (STATUS_REASON_TEXT VARCHAR(2000)). */
export const VIN_STATUS_REASON_MAX = 2000;
export const USER_STATUS_DISPLAY_DEFAULT = 'Unassigned';

export interface SIFTVehicle {
  vin: string;
  model_year: string;
  make: string;
  model: string;
  trim: string;
  color: string;
  vehicle_type: string;
  drivetrain: string;
  engine: string;
  body_style: string;
  doors: string;
  fuel_type: string;
  transmission: string;
  seller_id: string;
  seller_name: string;
  mid: number;
  list_time: string;
  list_days: number;
  days_since_first_offering?: number | null;
  channel: string;
  facilitation: string;
  site_location: string;
  list_status: string;
  list_expiration_time: string | null;
  days_expired: number | null;
  days_since_reprice: number | null;
  is_second_chance: number | null;
  latitude: number | null;
  longitude: number | null;
  base_mmr: number | null;
  mmr_range: string | null;
  adjusted_mmr: number | null;
  floor_price: number | null;
  buy_now_price: number | null;
  lowest_seller_counter: number | null;
  starting_bid_price?: number | null;
  allow_make_offer?: number | null;
  wholesale_supply_level: string | null;
  wholesale_demand_level: string | null;
  wholesale_avg_days_to_sell: number | null;
  buyer_coverage: number | null;
  wholesale_sales_efficiency: number | null;
  wholesale_sales_effectiveness: number | null;
  retail_supply_level: string | null;
  retail_demand_level: string | null;
  retail_avg_days_to_sell: number | null;
  retail_rmi: number | null;
  retail_spread: number | null;
  retail_retention: number | null;
  retail_avg_list_price: number | null;
  wholesale_days_supply: number | null;
  retail_days_supply: number | null;
  cohort_year_v4: string | null;
  cohort_mileage_v4: string | null;
  cohort_condition_v4: string | null;
  mileage: number | null;
  cr_grade: number | null;
  inspect_status: string;
  inspect_time: string | null;
  days_since_inspection: number | null;
  cr_link: string | null;
  inspection_channel: string | null;
  inspection_type: string | null;
  inspection_source: string | null;
  /** RDS-3.2 Manheim light: GREEN / YELLOW / RED / NOT_AS_IS; where it came from; title absent. */
  light_status: string | null;
  light_source: string | null;
  title_absent_flg: number | null;
  open_recalls: string[];
  damages: string[];
  recent_announcements: string[];
  structured_announcements: string | null;
  remarks: string | null;
  seller_comments: string | null;
  cr_items: string[];
  recon_catalog: ReconAction[];
  average_daily_vdp_views: number;
  total_client_vdp_views: number;
  client_workbook_saves: number;
  simulcast_logged_in: string;
  photo_count: number;
  state: string;
  /** §4.3 where the vehicle is (from its ZIP); `state` is the auction's state. */
  location_state: string | null;
  auction: string;
  region: string;
  zip_code: string;
  
  // User-assigned status
  user_status: UserVinStatus;
  
  // Territory fields (new columns from Snowflake)
  seller_zip_code: string;
  data_source: string;
  territory_id: string;
  parent_territory_id: string;
  grandparent_territory_id: string;
  territory_owner: string;
  manager: string;
  director: string;
  territory_zip: string;
  territory_city: string;
  territory_state: string;
  territory_market_center: string;
  rds_territory: string;
  
  seller_acquisition_time: string;
  seller_ownership_days: number;
  seller_acquisition_method: string;
  bid_history: BidRecord[];
  active_bids: number;
  total_bids: number;
  active_high_bid: number | null;
  listing_history: ListingRecord[];
  listing_occurences: number;
  vdp_url: string | null;
  retail_value: number | null;
  retail_value_range: string | null;
  retail_list_price: number | null;
  adj_mmr_source?: string | null;
  adj_mmr_as_of?: string | null;
  retail_value_source?: string | null;
  retail_value_as_of?: string | null;
}

export interface BidRecord {
  bid_time: string;
  bid_offer_type: string;
  closed_time: string;
  buyer_id: string;
  buyer_name: string;
  bid_amt: number;
  bid_status: string;
  bid_type: string;
  bid_channel: string;
}

export interface ListingRecord {
  list_time: string;
  list_status: string;
  lat: string;
  lon: string;
  state: string;
  auction: string;
  channel: string;
  cr_grade: string;
  mileage: string;
  adj_mmr: string;
  buy_now_price: string;
  floor_price: string;
  mmr_range: string;
  sold_amt: string;
  sold_time: string;
  seller_id: string;
  seller_name: string;
  buyer_id: string;
  buyer_name: string;
  simulcast_logged_in: number | null;
  client_vdp_views: number | null;
  client_workbook_saves: number | null;
  vdp_url: string | null;
}

export interface ReconAction {
  item_name: string;
  est_repair_cost: number;
  est_repair_days: number;
}
